import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Layout,
  Select,
  Upload,
  Typography,
  message,
  Card,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { InboxOutlined } from "@ant-design/icons";
import { Get } from "../../config/api/get";
import { Post } from "../../config/api/post";
import { ASSETS, COURSE, CONTENT_TYPE } from "../../config/constants/api";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";
import "../quiz-management/quiz-details.css";
import "./asset-management.css";

const { TextArea } = Input;

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const ACCEPT_BY_TYPE = {
  IMAGE: "image/*",
  VIDEO: "video/*",
  PDF: ".pdf,application/pdf",
};

function AssetAdd() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const userData = useSelector((state) => state.user.userData);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedType, setSelectedType] = useState("IMAGE");

  useEffect(() => {
    loadCourses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCourses = async () => {
    try {
      const res = await Get(COURSE.getAllCourses, token, { limit: "500", createdBy: userData?._id });
      const data = res?.data ?? res;
      const list = data?.docs ?? data ?? [];
      setCourses(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error("Failed to load courses");
    }
  };

  const beforeUpload = (file) => {
    const type = form.getFieldValue("type") || selectedType;
    const accept = ACCEPT_BY_TYPE[type] || "";
    const isImage = type === "IMAGE" && file.type.startsWith("image/");
    const isVideo = type === "VIDEO" && file.type.startsWith("video/");
    const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
    if (!isImage && !isVideo && !isPdf) {
      message.error(`File type must match asset type "${type}". Allowed: ${accept}`);
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    return false;
  };

  const onFinish = (values) => {
    const type = values.type || "IMAGE";
    const file = fileList[0];
    if (!file) {
      message.error("Please upload a file");
      return;
    }
    const isImage = type === "IMAGE" && file.type?.startsWith("image/");
    const isVideo = type === "VIDEO" && file.type?.startsWith("video/");
    const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
    if (!isImage && !isVideo && !isPdf) {
      message.error(`File type must match asset type "${type}"`);
      return;
    }

    const formData = new FormData();
    if (type === "PDF") {
      formData.append("files", file);
    } else if (type === "IMAGE") {
      formData.append("image", file);
    } else if (type === "VIDEO") {
      formData.append("video", file);
    }
    formData.append("title", values.title);
    formData.append("description", values.description || "");
    formData.append("type", type);
    formData.append("status", values.status || "ACTIVE");
    if (values.courses && values.courses.length) {
      values.courses.forEach((courseId) => formData.append("courses", courseId));
    }

    setLoading(true);
    Post(ASSETS.createAsset, formData, token, null, CONTENT_TYPE.FORM_DATA)
      .then((res) => {
        console.log(res, 'res')
        setLoading(false);
        const data = res?.data ?? res;
        if (data?.status) {
          swal("Success!", "Asset created successfully", "success");
          navigate("/asset-management");
        } else {
          swal("Error", data?.message || "Failed to create asset", "error");
        }
      })
      .catch(() => setLoading(false));
  };

  return (
    <Layout
      className="configuration upcoming-lessons-page quiz-details-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr asset-management-hero">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
        >
          Resource Library
        </Typography.Title>
      </Row>

      <div className="boxDetails quiz-details-shell">
        <Card className="quiz-details-top-card" bordered={false}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24}>
              <div className="course-management-title-block">
                <button
                  type="button"
                  className="course-management-inline-back"
                  aria-label="Go back"
                  onClick={() => navigate(-1)}
                >
                  <FaArrowLeft aria-hidden="true" />
                </button>
                <Typography.Title
                  level={2}
                  className="fontFamily1 quiz-details-page-title"
                >
                  Add Asset
                </Typography.Title>
              </div>
            </Col>
          </Row>
        </Card>

        <div className="quiz-details-panel">
              <Form
                className="quiz-details-form"
                form={form}
                onFinish={onFinish}
                autoComplete="off"
                initialValues={{ status: "ACTIVE", type: "IMAGE" }}
              >
                <Row className="quiz-details-form-inner" gutter={[20, 20]} style={{ padding: "8px 0" }}>
                  <Col xs={24} md={18}>

                    {/* Row 1: Title + Type */}
                    <Row gutter={20}>
                      <Col xs={24} sm={12} className="quiz-details-field-col">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          Title :
                        </Typography.Title>
                        <Form.Item
                          name="title"
                          style={{ width: "100%" }}
                          rules={[{ required: true, message: "Please enter title" }]}
                        >
                          <Input
                            size="large"
                            placeholder="Enter asset title"
                            className="signupFormInput"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} className="quiz-details-field-col">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          Type :
                        </Typography.Title>
                        <Form.Item
                          name="type"
                          style={{ width: "100%" }}
                          rules={[{ required: true, message: "Please select a type" }]}
                        >
                          <Select
                            size="large"
                            placeholder="Select type"
                            className="signupSelectBox"
                            options={ASSET_TYPES}
                            onChange={(v) => {
                              setSelectedType(v);
                              setFileList([]);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 2: Status + Courses */}
                    <Row gutter={20}>
                      <Col xs={24} sm={12} className="quiz-details-field-col">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          Status :
                        </Typography.Title>
                        <Form.Item name="status" style={{ width: "100%" }}>
                          <Select
                            size="large"
                            className="signupSelectBox"
                            options={STATUS_OPTIONS}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} className="quiz-details-field-col">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          Courses :
                        </Typography.Title>
                        <Form.Item name="courses" style={{ width: "100%" }}>
                          <Select
                            mode="multiple"
                            size="large"
                            placeholder="Select courses"
                            className="custum-multiselect courseSelectBox2"
                            optionFilterProp="label"
                            options={courses.map((c) => ({ value: c._id, label: c.title || c.name }))}
                            filterOption={(input, option) =>
                              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 3: Description (full width) */}
                    <Row gutter={20}>
                      <Col xs={24} className="quiz-details-field-col">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          Description :
                        </Typography.Title>
                        <Form.Item name="description" style={{ width: "100%" }}>
                          <TextArea
                            rows={4}
                            placeholder="Enter description"
                            className="signupFormInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Row 4: File Upload (full width) */}
                    <Row gutter={20}>
                      <Col xs={24} className="quiz-details-field-col asset-upload-dragger">
                        <Typography.Title
                          level={5}
                          className="fontFamily1 quiz-details-field-label"
                        >
                          File Upload :
                        </Typography.Title>
                        <Form.Item
                          style={{ width: "100%" }}
                          extra={`Allowed for ${selectedType}: ${ACCEPT_BY_TYPE[selectedType] || "—"}`}
                          required
                        >
                          <Upload.Dragger
                            maxCount={1}
                            fileList={fileList.map((f) => ({ uid: f.uid || "-", name: f.name, status: "done" }))}
                            beforeUpload={beforeUpload}
                            onRemove={() => setFileList([])}
                            accept={ACCEPT_BY_TYPE[selectedType]}
                          >
                            <p className="ant-upload-drag-icon">
                              <InboxOutlined style={{ fontSize: 48, color: "#2484c6" }} />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area</p>
                            <p className="ant-upload-hint">Supports single file upload</p>
                          </Upload.Dragger>
                        </Form.Item>
                      </Col>
                    </Row>

                  </Col>
                </Row>

                <div className="quiz-details-form-actions" style={{ paddingTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="loginButton"
                    loading={loading}
                  >
                    {loading ? "Saving..." : "Save Asset"}
                  </Button>
                  <Button
                    size="large"
                    className="fontFamily1 quiz-details-cancel"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
        </div>
      </div>
    </Layout>
  );
}

export default AssetAdd;
